import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  getDashboardMetrics, 
  getShipments, 
  getCatalog, 
  getContainerEvolution,
  getAvailableWidgets,
  getDashboardPreferences,
  updateDashboardPreferences,
  getWidgetData,
  getDeliveredContainers,
} from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { DeliveryTimesWidget } from '../components/dashboard/DeliveryTimesWidget';
import { MonthlySummaryWidget } from '../components/dashboard/MonthlySummaryWidget';
import { ExpiringCertificatesWidget } from '../components/dashboard/ExpiringCertificatesWidget';
import { StaleSyncWidget } from '../components/dashboard/StaleSyncWidget';
import { WidgetSettingsDialog } from '../components/dashboard/WidgetSettings';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';
import {
  Ship,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Plus,
  ArrowRight,
  Calendar,
  Settings,
  TrendingUp,
  TrendingDown,
  Building2,
  DollarSign,
  Activity,
  Anchor,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Timer,
  Calculator,
  Loader2,
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const GRADIENT_COLORS = {
  blue: ['#3b82f6', '#1d4ed8'],
  green: ['#10b981', '#059669'],
  amber: ['#f59e0b', '#d97706'],
  purple: ['#8b5cf6', '#7c3aed'],
};

// Animated Counter Component
function AnimatedNumber({ value, duration = 1000 }) {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let startTime;
    const startValue = displayValue;
    const endValue = value || 0;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.floor(startValue + (endValue - startValue) * easeOut));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value]);
  
  return <span className="number-animate">{displayValue.toLocaleString()}</span>;
}

// Enhanced KPI Card Component
function KPICard({ icon: Icon, label, value, subLabel, color, delay, trend }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/5 text-blue-400',
    green: 'from-emerald-500/20 to-emerald-600/5 text-emerald-400',
    amber: 'from-amber-500/20 to-amber-600/5 text-amber-400',
    purple: 'from-purple-500/20 to-purple-600/5 text-purple-400',
  };
  
  const accentColors = {
    blue: '#3b82f6',
    green: '#10b981',
    amber: '#f59e0b',
    purple: '#8b5cf6',
  };

  return (
    <div 
      className={`kpi-card dashboard-card bg-card border border-border rounded-lg p-5 opacity-0 animate-fade-in-up stagger-${delay}`}
      style={{ '--kpi-accent-color': accentColors[color] }}
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
            trend >= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-4xl font-heading text-foreground">
          <AnimatedNumber value={value} />
        </p>
        <p className="text-sm text-muted-foreground mt-1">{label}</p>
        {subLabel && (
          <p className="text-xs text-muted-foreground/70 mt-0.5">{subLabel}</p>
        )}
      </div>
    </div>
  );
}

// Enhanced Status Chart with better visuals - NOW BASED ON CONTAINERS
function StatusChartWidget({ data, statusLabels }) {
  const { t } = useI18n();
  const chartData = useMemo(() => {
    return data?.containers_by_status 
      ? Object.entries(data.containers_by_status).map(([key, value]) => ({
          name: statusLabels[key] || key,
          value,
          code: key,
        }))
      : [];
  }, [data, statusLabels]);

  const total = chartData.reduce((acc, item) => acc + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      return (
        <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-4 py-3 shadow-xl backdrop-blur-sm">
          <p className="text-white font-semibold text-sm">{payload[0].name}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-2xl font-heading text-white">{payload[0].value}</p>
            <p className="text-slate-400 text-sm">({percentage}%)</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-card chart-container bg-card border border-border rounded-lg p-5 opacity-0 animate-scale-in stagger-3">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl text-foreground">{t('dashboard.containerStatus')}</h3>
          <p className="text-sm text-muted-foreground">{t('dashboard.currentDistribution')}</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/50 rounded-full">
          <Activity size={14} className="text-primary" />
          <span className="text-sm font-medium">{total} total</span>
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <defs>
              {COLORS.map((color, index) => (
                <linearGradient key={`gradient-${index}`} id={`pieGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={1} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              paddingAngle={3}
              dataKey="value"
              animationBegin={200}
              animationDuration={800}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={`url(#pieGradient-${index % COLORS.length})`}
                  stroke="transparent"
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-border/50">
        {chartData.map((item, idx) => (
          <div key={item.name} className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/30 transition-colors cursor-default">
            <span 
              className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm" 
              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
            />
            <span className="text-sm text-foreground truncate">{item.name}</span>
            <span className="text-sm font-mono text-muted-foreground ml-auto">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Enhanced Evolution Chart with Area + Line
function EvolutionChartWidget({ data, loading, totals, hasFilter }) {
  const { t } = useI18n();
  const chartData = useMemo(() => {
    return data?.map(d => ({
      period: d.label || d.period,
      Arribos: d.arrivals,
      Entregas: d.deliveries,
    })) || [];
  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-4 py-3 shadow-xl backdrop-blur-sm">
          <p className="text-white font-semibold mb-2">{label}</p>
          {payload.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 text-sm">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-slate-300">{item.name}:</span>
              <span className="text-white font-medium ml-auto">{item.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-card chart-container bg-card border border-border rounded-lg p-5 opacity-0 animate-scale-in stagger-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl text-foreground">{t('dashboard.containerEvolution')}</h3>
          <p className="text-sm text-muted-foreground">{t('dashboard.arrivalsVsDeliveries')}</p>
        </div>
        {totals && (
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-sm text-muted-foreground">{t('dashboard.arrivals')}:</span>
              <span className="text-sm font-medium text-blue-400">{totals.arrivals}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-sm text-muted-foreground">{t('dashboard.deliveriesLabel')}:</span>
              <span className="text-sm font-medium text-emerald-400">{totals.deliveries}</span>
            </div>
          </div>
        )}
      </div>
      <div className="h-72">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            {t('dashboard.noDataInRange')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <defs>
                <linearGradient id="arriboGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="entregaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 11, fill: '#94a3b8' }} 
                stroke="#334155"
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#94a3b8' }} 
                stroke="#334155"
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey={t('dashboard.arrivals')} 
                fill="url(#arriboGradient)" 
                stroke="#3b82f6"
                strokeWidth={2}
                animationDuration={1000}
              />
              <Area 
                type="monotone" 
                dataKey={t('dashboard.deliveriesLabel')} 
                fill="url(#entregaGradient)" 
                stroke="#10b981"
                strokeWidth={2}
                animationDuration={1000}
                animationBegin={200}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      {!hasFilter && (
        <p className="text-xs text-muted-foreground mt-3 text-center">
          {t('dashboard.monthlyHistoric')}. {t('dashboard.useDateFilterFull')}
        </p>
      )}
    </div>
  );
}

// Enhanced Clients Chart - CONTAINER CENTRIC
function ClientsChartWidget({ data, widgetData, loading }) {
  const { t } = useI18n();
  const chartData = useMemo(() => {
    // Prefer container-based data from metrics
    if (data?.containers_by_client?.length > 0) {
      return data.containers_by_client.map(item => ({
        name: item.client_name || t('dashboard.noClient'),
        count: item.count,
      }));
    }
    return widgetData?.data?.map(item => ({
      name: item.client_name || t('dashboard.noClient'),
      count: item.count,
    })) || [];
  }, [data, widgetData]);

  if (loading) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-xl text-foreground mb-4">{t('dashboard.topClients')}</h3>
        <div className="h-56 flex items-center justify-center text-muted-foreground animate-pulse">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) return null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-4 py-3 shadow-xl backdrop-blur-sm">
          <p className="text-white font-semibold">{label}</p>
          <p className="text-2xl font-heading text-primary mt-1">{payload[0].value}</p>
          <p className="text-slate-400 text-sm">{t('dashboard.containersUnit')}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-card chart-container bg-card border border-border rounded-lg p-5 opacity-0 animate-scale-in stagger-5">
      <div className="flex items-center gap-2 mb-4">
        <Building2 size={20} className="text-primary" />
        <h3 className="font-heading text-xl text-foreground">{t('dashboard.topClientsByContainers')}</h3>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" barSize={20}>
            <defs>
              <linearGradient id="clientBarGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                <stop offset="100%" stopColor="#60a5fa" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#334155" />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={120} 
              tick={{ fontSize: 11, fill: '#94a3b8' }} 
              stroke="#334155"
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--secondary) / 0.3)' }} />
            <Bar 
              dataKey="count" 
              fill="url(#clientBarGradient)" 
              radius={[0, 6, 6, 0]}
              animationDuration={800}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Recent Shipments Widget
function RecentShipmentsWidget({ shipments }) {
  const { t } = useI18n();
  return (
    <div className="dashboard-card bg-card border border-border rounded-lg opacity-0 animate-scale-in stagger-6">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div className="flex items-center gap-2">
          <Ship size={20} className="text-primary" />
          <h3 className="font-heading text-xl">{t('dashboard.recentShipments')}</h3>
        </div>
        <Link to="/shipments">
          <Button variant="ghost" size="sm" className="gap-1">
            {t('dashboard.viewAll')}
            <ArrowRight size={14} />
          </Button>
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
              <th className="py-3 px-4 font-medium">{t('dashboard.mblBl')}</th>
              <th className="py-3 px-4 font-medium">{t('dashboard.client')}</th>
              <th className="py-3 px-4 font-medium">{t('dashboard.statusCol')}</th>
              <th className="py-3 px-4 font-medium">{t('dashboard.containersCol')}</th>
              <th className="py-3 px-4 font-medium">{t('dashboard.lastUpdate')}</th>
            </tr>
          </thead>
          <tbody>
            {shipments.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-muted-foreground">
                  <Ship size={32} className="mx-auto mb-2 opacity-30" />
                  {t('dashboard.noShipmentsRegistered')}
                </td>
              </tr>
            ) : (
              shipments.map((shipment, idx) => (
                <tr 
                  key={shipment.mbl} 
                  className="border-b border-border/30 hover:bg-secondary/20 transition-colors"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <td className="py-3 px-4">
                    <Link to={`/shipments/${shipment.mbl}`} className="font-mono text-sm text-primary hover:underline">
                      {shipment.mbl}
                    </Link>
                  </td>
                  <td className="py-3 px-4 text-sm">{shipment.client_name || '—'}</td>
                  <td className="py-3 px-4">
                    <StatusBadge code={shipment.status_general_code} label={shipment.status_label} />
                  </td>
                  <td className="py-3 px-4 text-sm font-mono">
                    <span className="inline-flex items-center gap-1">
                      {shipment.container_count}
                      {shipment.container_count === 0 && (
                        <AlertTriangle size={14} className="text-amber-400" />
                      )}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(shipment.last_update_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Port Alerts Widget
function PortAlertsWidget({ data, loading }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  
  if (loading) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
          <AlertTriangle className="text-amber-400" size={20} />
          {t('dashboard.portAlerts')}
        </h3>
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const alerts = data?.alerts || [];
  const displayAlerts = expanded ? alerts : alerts.slice(0, 5);

  return (
    <div className="dashboard-card glass-widget rounded-lg p-5 opacity-0 animate-scale-in stagger-3">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <AlertTriangle className="text-amber-400" size={18} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-foreground">{t('dashboard.portAlerts')}</h3>
            <p className="text-xs text-muted-foreground">+7 {t('dashboard.daysInPortLabel')}</p>
          </div>
        </div>
        {alerts.length > 0 && (
          <span className="text-sm bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full font-medium">
            {alerts.length}
          </span>
        )}
      </div>
      
      {alerts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <CheckCircle size={32} className="mx-auto mb-2 text-emerald-400/50" />
          <p className="text-sm">{t('dashboard.noActiveAlerts')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {displayAlerts.map((alert, idx) => (
              <div 
                key={idx} 
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div>
                  <Link 
                    to={`/shipments/${alert.shipment_mbl}`} 
                    className="font-mono text-sm text-primary hover:underline"
                  >
                    {alert.container_number}
                  </Link>
                  <p className="text-xs text-muted-foreground mt-0.5">{alert.shipment_mbl}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  alert.days_in_port > 14 
                    ? 'bg-red-500/20 text-red-400' 
                    : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {alert.days_in_port} {t('dashboard.days')}
                </span>
              </div>
            ))}
          </div>
          {alerts.length > 5 && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-3 py-2 text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 transition-colors"
            >
              {expanded ? (
                <>{t('dashboard.viewLess')} <ChevronUp size={14} /></>
              ) : (
                <>{t('dashboard.viewMore')} {alerts.length - 5} {t('dashboard.more')} <ChevronDown size={14} /></>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Pending Costs Widget
function PendingCostsWidget({ data, loading }) {
  const { t } = useI18n();
  if (loading) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground mb-4">{t('dashboard.pendingCosts')}</h3>
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const pending = data?.pending || [];

  return (
    <div className="dashboard-card glass-widget rounded-lg p-5 opacity-0 animate-scale-in stagger-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <DollarSign className="text-blue-400" size={18} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-foreground">{t('dashboard.pendingCosts')}</h3>
            <p className="text-xs text-muted-foreground">{t('dashboard.notCalculated')}</p>
          </div>
        </div>
        {pending.length > 0 && (
          <span className="text-sm bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full font-medium">
            {pending.length}
          </span>
        )}
      </div>
      
      {pending.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <CheckCircle size={32} className="mx-auto mb-2 text-emerald-400/50" />
          <p className="text-sm">{t('dashboard.allCostsCalculated')}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {pending.slice(0, 10).map((item, idx) => (
            <div 
              key={idx} 
              className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
            >
              <Link to={`/shipments/${item.mbl}`} className="font-mono text-sm text-primary hover:underline">
                {item.mbl}
              </Link>
              <span className="text-sm text-muted-foreground">{item.client_name || t('dashboard.noClient')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Delivered Containers Widget
function DeliveredContainersWidget({ deliveredContainers, loading, dateRange }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  
  const filteredContainers = useMemo(() => {
    return deliveredContainers.filter(c => {
      if (!dateRange.from && !dateRange.to) return true;
      const deliveryDate = c.delivered_at?.slice(0, 10);
      if (!deliveryDate) return false;
      if (dateRange.from && deliveryDate < dateRange.from) return false;
      if (dateRange.to && deliveryDate > dateRange.to) return false;
      return true;
    });
  }, [deliveredContainers, dateRange]);

  if (loading) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground mb-4">{t('dashboard.deliveredContainers')}</h3>
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const displayContainers = expanded ? filteredContainers : filteredContainers.slice(0, 5);

  return (
    <div className="dashboard-card glass-widget rounded-lg p-5 opacity-0 animate-scale-in stagger-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <CheckCircle className="text-emerald-400" size={18} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-foreground">{t('dashboard.deliveredTitle')}</h3>
            <p className="text-xs text-muted-foreground">{t('dashboard.containersCompleted')}</p>
          </div>
        </div>
        {filteredContainers.length > 0 && (
          <span className="text-sm bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full font-medium">
            {filteredContainers.length}
          </span>
        )}
      </div>
      
      {filteredContainers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Package size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No hay entregas en el período</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {displayContainers.map((c, idx) => (
              <div 
                key={idx} 
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div>
                  <p className="font-mono text-sm text-foreground">{c.container_number}</p>
                  <Link 
                    to={`/shipments/${encodeURIComponent(c.shipment_mbl.trim())}`} 
                    className="text-xs text-primary hover:underline"
                  >
                    {c.shipment_mbl}
                  </Link>
                </div>
                <span className="text-xs text-emerald-400">{formatDate(c.delivered_at)}</span>
              </div>
            ))}
          </div>
          {filteredContainers.length > 5 && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-3 py-2 text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 transition-colors"
            >
              {expanded ? (
                <>{t('dashboard.viewLess')} <ChevronUp size={14} /></>
              ) : (
                <>{t('dashboard.viewMore')} {filteredContainers.length - 5} {t('dashboard.more')} <ChevronDown size={14} /></>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Importer Chart Widget - CONTAINER CENTRIC
function ImporterChartWidget({ data, loading, metricsData }) {
  const { t } = useI18n();
  if (loading) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground mb-4">{t('dashboard.containersByImporter')}</h3>
        <div className="h-56 flex items-center justify-center text-muted-foreground animate-pulse">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  // Prefer container-based data from metrics
  const chartData = metricsData?.containers_by_importer?.length > 0
    ? metricsData.containers_by_importer.map(item => ({
        name: item.importer_name || t('dashboard.noImporter'),
        count: item.count,
      }))
    : data?.data?.map(item => ({
        name: item.importer_name || t('dashboard.noImporter'),
        count: item.count,
      })) || [];

  if (chartData.length === 0) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground mb-4">{t('dashboard.containersByImporter')}</h3>
        <div className="h-56 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <Building2 size={32} className="mx-auto mb-2 opacity-30" />
            <p>{t('common.noData')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card chart-container bg-card border border-border rounded-lg p-5 opacity-0 animate-scale-in stagger-4">
      <div className="flex items-center gap-2 mb-4">
        <Building2 size={20} className="text-purple-400" />
        <h3 className="font-heading text-lg text-foreground">{t('dashboard.containersByImporter')}</h3>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" barSize={16}>
            <defs>
              <linearGradient id="importerGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#a78bfa" />
              </linearGradient>
            </defs>
            <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#334155" />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={100} 
              tick={{ fontSize: 10, fill: '#94a3b8' }} 
              stroke="#334155"
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload?.length) {
                  return (
                    <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-4 py-3 shadow-xl">
                      <p className="text-white font-semibold">{label}</p>
                      <p className="text-xl font-heading text-purple-400">{payload[0].value}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="count" fill="url(#importerGradient)" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Monthly Summary Widget
// Quick Date Presets Component
function DatePresets({ onSelect, active }) {
  const { t } = useI18n();
  const presets = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
    { label: t('dashboard.year'), days: 365 },
  ];

  return (
    <div className="flex gap-1">
      {presets.map(preset => (
        <button
          key={preset.label}
          onClick={() => onSelect(preset.days)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            active === preset.days 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
          }`}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}

// Main Dashboard Component
export default function DashboardPage() {
  const { t } = useI18n();
  const [metrics, setMetrics] = useState(null);
  const [recentShipments, setRecentShipments] = useState([]);
  const [statusLabels, setStatusLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [evolutionData, setEvolutionData] = useState([]);
  const [evolutionTotals, setEvolutionTotals] = useState(null);
  const [evolutionHasFilter, setEvolutionHasFilter] = useState(true);
  const [evolutionLoading, setEvolutionLoading] = useState(false);
  const [deliveredContainers, setDeliveredContainers] = useState([]);
  const [deliveredLoading, setDeliveredLoading] = useState(false);
  
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [preferences, setPreferences] = useState({ widgets: [] });
  const [widgetData, setWidgetData] = useState({});
  const [widgetLoading, setWidgetLoading] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [activePreset, setActivePreset] = useState(null);

  useEffect(() => {
    loadPreferences();
    loadStatusLabels();
    loadDeliveredContainers();
  }, []);

  useEffect(() => {
    loadData();
    loadEvolutionData();
    loadDeliveredContainers();
  }, [dateRange]);

  useEffect(() => {
    const customWidgets = ['port_alerts', 'pending_costs', 'importer_chart', 'monthly_summary', 'clients_chart', 'expiring_certificates', 'stale_sync'];
    preferences.widgets
      .filter(w => w.enabled && customWidgets.includes(w.widget_id))
      .forEach(w => loadWidgetData(w.widget_id));
  }, [preferences, dateRange]);

  const loadDeliveredContainers = async () => {
    setDeliveredLoading(true);
    try {
      const params = {};
      if (dateRange.from) params.from_date = dateRange.from;
      if (dateRange.to) params.to_date = dateRange.to;
      const res = await getDeliveredContainers(params);
      setDeliveredContainers(res.data);
    } catch (err) {
      console.error('Error loading delivered containers:', err);
    } finally {
      setDeliveredLoading(false);
    }
  };

  const loadStatusLabels = async () => {
    try {
      const statusRes = await getCatalog('shipment_status');
      const labels = {};
      statusRes.data.forEach(s => { labels[s.code] = s.label; });
      setStatusLabels(labels);
    } catch (err) {
      console.error('Error loading status labels:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      
      const [metricsRes, shipmentsRes] = await Promise.all([
        getDashboardMetrics(params),
        getShipments({ is_active: true }),
      ]);
      
      setMetrics(metricsRes.data);
      setRecentShipments(shipmentsRes.data.slice(0, 5));
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const [widgetsRes, prefsRes] = await Promise.all([
        getAvailableWidgets(),
        getDashboardPreferences()
      ]);
      setAvailableWidgets(widgetsRes.data);
      setPreferences(prefsRes.data);
    } catch (err) {
      console.error('Error loading preferences:', err);
    }
  };

  const loadEvolutionData = async () => {
    setEvolutionLoading(true);
    try {
      const params = { group_by: 'month' };
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      
      const res = await getContainerEvolution(params);
      setEvolutionData(res.data.data);
      setEvolutionTotals(res.data.totals);
      setEvolutionHasFilter(res.data.has_filter);
    } catch (err) {
      console.error('Error loading evolution data:', err);
    } finally {
      setEvolutionLoading(false);
    }
  };

  const handleDatePreset = (days) => {
    const to = new Date().toISOString().split('T')[0];
    const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setDateRange({ from, to });
    setActivePreset(days);
  };

  const clearDateFilter = () => {
    setDateRange({ from: '', to: '' });
    setActivePreset(null);
  };

  const loadWidgetData = async (widgetId) => {
    setWidgetLoading(prev => ({ ...prev, [widgetId]: true }));
    try {
      const params = {};
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      
      const res = await getWidgetData(widgetId, params);
      setWidgetData(prev => ({ ...prev, [widgetId]: res.data }));
    } catch (err) {
      console.error(`Error loading widget ${widgetId}:`, err);
    } finally {
      setWidgetLoading(prev => ({ ...prev, [widgetId]: false }));
    }
  };

  const handleSavePreferences = async (newPrefs) => {
    try {
      await updateDashboardPreferences({ widgets: newPrefs.widgets });
      setPreferences(newPrefs);
      toast.success(t('dashboard.preferencesSaved'));
    } catch (err) {
      toast.error(t('dashboard.preferencesError'));
    }
  };

  const isWidgetEnabled = (widgetId) => {
    const widget = preferences.widgets.find(w => w.widget_id === widgetId);
    return widget?.enabled ?? true;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl gradient-text">{t('dashboard.title')}</h1>
            <p className="text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setSettingsOpen(true)}
              data-testid="customize-dashboard-btn"
              className="gap-2"
            >
              <Settings size={16} />
              <span className="hidden sm:inline">{t('dashboard.customize')}</span>
            </Button>
            <Link to="/shipments/new">
              <Button data-testid="new-shipment-btn" className="gap-2">
                <Plus size={16} />
                <span className="hidden sm:inline">{t('dashboard.newShipment')}</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Date Filter Bar */}
        <div className="dashboard-card bg-card/50 border border-border rounded-xl p-4 opacity-0 animate-fade-in-up stagger-1">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={16} className="text-primary" />
              <span className="font-medium">{t('dashboard.periodFilterLabel')}</span>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => {
                    setDateRange(prev => ({ ...prev, from: e.target.value }));
                    setActivePreset(null);
                  }}
                  className="w-36 text-sm h-9"
                  data-testid="date-from"
                />
                <span className="text-muted-foreground">—</span>
                <Input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => {
                    setDateRange(prev => ({ ...prev, to: e.target.value }));
                    setActivePreset(null);
                  }}
                  className="w-36 text-sm h-9"
                  data-testid="date-to"
                />
              </div>
              
              <div className="h-6 w-px bg-border hidden sm:block" />
              
              <DatePresets onSelect={handleDatePreset} active={activePreset} />
              
              {(dateRange.from || dateRange.to) && (
                <Button variant="ghost" size="sm" onClick={clearDateFilter} className="text-xs">
                  Limpiar
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* KPIs Grid - CONTAINER CENTRIC */}
        {isWidgetEnabled('kpis') && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KPICard 
              icon={Package} 
              label={t('dashboard.totalContainers')} 
              value={metrics?.total_containers}
              subLabel={`${metrics?.total_shipments || 0} ${t('shipments.totalShipments')}`}
              color="blue"
              delay={1}
            />
            <KPICard 
              icon={Ship} 
              label={t('dashboard.inTransit')} 
              value={metrics?.containers_in_transit}
              subLabel={t('dashboard.navigating')}
              color="green"
              delay={2}
            />
            <KPICard 
              icon={Anchor} 
              label={t('dashboard.inPort')} 
              value={metrics?.containers_in_port}
              subLabel={t('dashboard.waitingDelivery')}
              color="amber"
              delay={3}
            />
            <KPICard 
              icon={CheckCircle} 
              label={t('dashboard.delivered')} 
              value={metrics?.containers_delivered}
              color="purple"
              delay={4}
            />
            <KPICard 
              icon={Timer} 
              label={t('dashboard.avgDaysInPort')} 
              value={metrics?.avg_days_in_port}
              subLabel={t('dashboard.arrivalToDelivery')}
              color="blue"
              delay={5}
            />
          </div>
        )}

        {/* Main Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {isWidgetEnabled('status_chart') && (
            <StatusChartWidget data={metrics} statusLabels={statusLabels} />
          )}
          {isWidgetEnabled('evolution_chart') && (
            <EvolutionChartWidget 
              data={evolutionData} 
              loading={evolutionLoading} 
              totals={evolutionTotals}
              hasFilter={evolutionHasFilter}
            />
          )}
        </div>

        {/* Secondary Widgets Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isWidgetEnabled('port_alerts') && (
            <PortAlertsWidget data={widgetData.port_alerts} loading={widgetLoading.port_alerts} />
          )}
          {isWidgetEnabled('pending_costs') && (
            <PendingCostsWidget data={widgetData.pending_costs} loading={widgetLoading.pending_costs} />
          )}
          {isWidgetEnabled('delivered_containers') && (
            <DeliveredContainersWidget 
              deliveredContainers={deliveredContainers} 
              loading={deliveredLoading} 
              dateRange={dateRange} 
            />
          )}
          {isWidgetEnabled('expiring_certificates') && (
            <ExpiringCertificatesWidget 
              data={widgetData.expiring_certificates} 
              loading={widgetLoading.expiring_certificates} 
            />
          )}
          {isWidgetEnabled('stale_sync') && (
            <StaleSyncWidget
              data={widgetData.stale_sync}
              loading={widgetLoading.stale_sync}
            />
          )}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {isWidgetEnabled('clients_chart') && (
            <ClientsChartWidget 
              data={metrics} 
              widgetData={widgetData.clients_chart} 
              loading={widgetLoading.clients_chart} 
            />
          )}
          {isWidgetEnabled('importer_chart') && (
            <ImporterChartWidget data={widgetData.importer_chart} loading={widgetLoading.importer_chart} metricsData={metrics} />
          )}
        </div>

        {/* Monthly Summary and ETA Prediction */}
        <div className="grid lg:grid-cols-2 gap-6">
          {isWidgetEnabled('monthly_summary') && (
            <MonthlySummaryWidget data={widgetData.monthly_summary} loading={widgetLoading.monthly_summary} />
          )}
          
          {/* Delivery Times / ETA Prediction Widget */}
          <DeliveryTimesWidget onCalculateEtas={loadData} />
        </div>

        {/* Recent Shipments */}
        {isWidgetEnabled('recent_shipments') && (
          <RecentShipmentsWidget shipments={recentShipments} />
        )}

        {/* Widget Settings Dialog */}
        <WidgetSettingsDialog
          availableWidgets={availableWidgets}
          preferences={preferences}
          onSave={handleSavePreferences}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      </div>
    </DashboardLayout>
  );
}
